/**
 * Native Authentication Service - Single Source of Truth
 *
 * Single Responsibility: Handle all authentication operations
 * Separation of Concerns: Isolate auth logic from UI and routing
 * DRY: Centralize authentication logic used across the app
 */

import { AuthApiClient } from "@/lib/api/clients/auth-api-client";
import { AppBaseService as BaseService } from "./base/app-base-service";
import { ServiceResult } from "./base/service-types";
import {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  VerifyEmailRequest,
  ResetPasswordRequest,
  ResendVerificationEmailRequest,
  MessageResponse,
  PublicConfigResponse,
} from "@/lib/types/api/auth-types";
import { User } from "@/lib/types/api/user-types";
import {
  clearAuthData,
  getAccessToken,
  isAuthenticated,
  getCurrentUser,
  storeAuthData,
} from "@/lib/http/auth-utils";

/**
 * Auth service for handling authentication operations
 * Follows SRP: Single responsibility for auth operations
 * Follows SOC: Separates auth business logic from HTTP concerns
 * Follows DRY: Uses enhanced base service patterns
 */
export class AuthService extends BaseService {
  readonly serviceName = "AuthService";

  constructor(private authApiClient: AuthApiClient) {
    super();
  }

  /**
   * Login with email and password
   * @param data - Login credentials
   * @returns Promise<ServiceResult<TokenResponse>>
   */
  async login(data: LoginRequest): Promise<ServiceResult<TokenResponse>> {
    // Validate required fields
    this.validateRequired(data as any, ["email", "password"], {
      operation: "login",
      entity: "auth",
    });

    return this.executeWithErrorHandling(() => this.authApiClient.login(data), {
      operation: "login",
      entity: "auth",
      metadata: { email: data.email },
    });
  }

  /**
   * Register new user account
   * @param data - Registration data
   * @returns Promise<ServiceResult<User>>
   */
  async register(data: RegisterRequest): Promise<ServiceResult<User>> {
    // Validate required fields
    this.validateRequired(data as any, ["email", "password"], {
      operation: "register",
      entity: "auth",
    });

    // Validate password length
    if (data.password.length < 8) {
      return this.createErrorResult({
        message: "Password must be at least 8 characters long",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "password",
            message: "Password must be at least 8 characters long",
            code: "MIN_LENGTH",
          },
        ],
      });
    }

    if (data.password !== data.confirm_password) {
      return this.createErrorResult({
        message: "Password must be the same as confirm password",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "confirm_password",
            message: "Password must be the same as confirm password",
            code: "SAME_AS_PASSWORD",
          },
        ],
      });
    }

    return this.executeWithErrorHandling(
      () => this.authApiClient.register(data),
      {
        operation: "register",
        entity: "auth",
        metadata: { email: data.email },
      }
    );
  }

  /**
   * Verify email with token
   * @param data - Email verification data
   * @returns Promise<ServiceResult<MessageResponse>>
   */
  async verifyEmail(
    data: VerifyEmailRequest
  ): Promise<ServiceResult<MessageResponse>> {
    this.validateRequired(data as any, ["token"], {
      operation: "verifyEmail",
      entity: "auth",
    });

    return this.executeWithErrorHandling(
      () => this.authApiClient.verifyEmail(data),
      {
        operation: "verifyEmail",
        entity: "auth",
      }
    );
  }

  /**
   * Request password recovery
   * @param email - User email address
   * @returns Promise<ServiceResult<MessageResponse>>
   */
  async requestPasswordRecovery(
    email: string
  ): Promise<ServiceResult<MessageResponse>> {
    if (!email || !this.isValidEmail(email)) {
      return this.createErrorResult({
        message: "Valid email address is required",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "email",
            message: "Valid email address is required",
            code: "INVALID_EMAIL",
          },
        ],
      });
    }

    return this.executeWithErrorHandling(
      () => this.authApiClient.requestPasswordRecovery(email),
      {
        operation: "requestPasswordRecovery",
        entity: "auth",
        metadata: { email },
      }
    );
  }

  /**
   * Reset password with token
   * @param data - Password reset data
   * @returns Promise<ServiceResult<MessageResponse>>
   */
  async resetPassword(
    data: ResetPasswordRequest
  ): Promise<ServiceResult<MessageResponse>> {
    this.validateRequired(data as any, ["token", "new_password"], {
      operation: "resetPassword",
      entity: "auth",
    });

    // Validate password length
    if (data.new_password.length < 8) {
      return this.createErrorResult({
        message: "Password must be at least 8 characters long",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "new_password",
            message: "Password must be at least 8 characters long",
            code: "MIN_LENGTH",
          },
        ],
      });
    }

    return this.executeWithErrorHandling(
      () => this.authApiClient.resetPassword(data),
      {
        operation: "resetPassword",
        entity: "auth",
      }
    );
  }

  /**
   * Resend verification email
   * @param data - Resend verification data
   * @returns Promise<ServiceResult<MessageResponse>>
   */
  async resendVerificationEmail(
    data: ResendVerificationEmailRequest
  ): Promise<ServiceResult<MessageResponse>> {
    this.validateRequired(data as any, ["email"], {
      operation: "resendVerificationEmail",
      entity: "auth",
    });

    if (!this.isValidEmail(data.email)) {
      return this.createErrorResult({
        message: "Valid email address is required",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "email",
            message: "Valid email address is required",
            code: "INVALID_EMAIL",
          },
        ],
      });
    }

    return this.executeWithErrorHandling(
      () => this.authApiClient.resendVerificationEmail(data),
      {
        operation: "resendVerificationEmail",
        entity: "auth",
        metadata: { email: data.email },
      }
    );
  }

  /**
   * Get public configuration
   * @returns Promise<ServiceResult<PublicConfigResponse>>
   */
  async getPublicConfig(): Promise<ServiceResult<PublicConfigResponse>> {
    return this.executeWithErrorHandling(
      () => this.authApiClient.getPublicConfig(),
      {
        operation: "getPublicConfig",
        entity: "auth",
      }
    );
  }

  /**
   * Validate email format
   * @param email - Email string to validate
   * @returns boolean indicating if email is valid
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // =============================================================================
  // STATIC UTILITY METHODS
  // These methods provide authentication utilities needed by other services
  // =============================================================================

  /**
   * Get current access token from localStorage
   * @returns string | null - The access token or null if not found
   */
  static getAccessToken(): string | null {
    return getAccessToken();
  }

  /**
   * Check if user is currently authenticated
   * @returns Promise<boolean> - True if authenticated, false otherwise
   */
  static async isAuthenticated(): Promise<boolean> {
    return isAuthenticated();
  }

  /**
   * Get current user from localStorage
   * @returns Promise<User | null> - The current user or null if not found
   */
  static async getCurrentUser(): Promise<User | null> {
    return getCurrentUser();
  }

  /**
   * Store authentication data in localStorage
   * @param tokenResponse - Token response from login/register
   * @param user - User object
   */
  static storeAuthData(tokenResponse: TokenResponse, user: User): void {
    return storeAuthData(tokenResponse, user);
  }

  /**
   * Clear all authentication data from localStorage
   */
  static clearAuthData(): void {
    return clearAuthData();
  }

  /**
   * Authenticate and get user in one operation
   * Used by auth context to check authentication status and get user data
   * @returns Promise<{isAuthenticated: boolean, user: User | null}>
   */
  static async authenticateAndGetUser(): Promise<{
    isAuthenticated: boolean;
    user: User | null;
  }> {
    const isAuth = await this.isAuthenticated();
    if (!isAuth) {
      return {
        isAuthenticated: false,
        user: null,
      };
    }

    // First try to get user from localStorage (fast path)
    let user = await this.getCurrentUser();

    // If no user in localStorage but we have auth token, fetch from backend
    if (!user) {
      try {
        const { UserApiClient } = await import(
          "@/lib/api/clients/user-api-client"
        );
        const userApiClient = new UserApiClient();
        user = await userApiClient.getMe();

        // Store user in localStorage for future use
        if (user) {
          const token = this.getAccessToken();
          if (token) {
            AuthService.storeAuthData(
              { access_token: token, token_type: "bearer" },
              user
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch user information:", error);
        // If we can't fetch user data, clear auth since something is wrong
        this.clearAuthData();
        return {
          isAuthenticated: false,
          user: null,
        };
      }
    }

    return {
      isAuthenticated: true,
      user,
    };
  }

  /**
   * Sign out user by clearing auth data
   * @returns Promise<void>
   */
  static async signOut(): Promise<void> {
    this.clearAuthData();

    // TODO: Call logout endpoint if needed
    // For now, just clear local data
  }
}

/**
 * Create AuthService instance
 * Use this factory function to create instances as needed to avoid circular dependencies
 */
export function createAuthService(): AuthService {
  return new AuthService(new AuthApiClient());
}
